import { csrfFetch, isCSRFTokenError, refreshCSRFToken } from '../utils/csrfFetch';
// constants
const SET_USER = "session/SET_USER";
const REMOVE_USER = "session/REMOVE_USER";
const CLEAR_SESSION = 'session/CLEAR_SESSION';
const SET_ACTIVE_SESSION = 'session/SET_ACTIVE_SESSION';
const CLEAR_ACTIVE_SESSION = 'session/CLEAR_ACTIVE_SESSION';
const UPDATE_FIRST_LOGIN = 'session/UPDATE_FIRST_LOGIN';

const setUser = (user) => ({
	type: SET_USER,
	payload: user,
});

const removeUser = () => ({
	type: REMOVE_USER,
});

const setActiveSession = (sessionInfo) => ({
    type: SET_ACTIVE_SESSION,
    payload: sessionInfo
});

const clearActiveSession = () => ({
    type: CLEAR_ACTIVE_SESSION
});

const updateFirstLogin = (value) => ({
    type: UPDATE_FIRST_LOGIN,
    payload: value
});

const initialState = {
    user: null,
    activeSession: null
};

export const clearSession = () => ({
    type: CLEAR_SESSION
});

export const authenticate = () => async (dispatch) => {
	try {
		const response = await csrfFetch("/api/auth/");
		if (!response.ok) {
			dispatch(removeUser());
			return null;
		}
		const data = await response.json();
		if (data.errors) {
			dispatch(removeUser());
			return;
		}

		// Store the session ID in localStorage if it's returned
		if (data.session_id) {
			localStorage.setItem('sessionId', data.session_id);
		}

		dispatch(setUser(data));
	} catch (error) {
		dispatch(removeUser());
	}
};

export const login = (email, password, forceLogin = false, agreedToTerms = false) => async (dispatch) => {
	try {
		console.log("Login attempt with force_login:", forceLogin, "agreed to terms:", agreedToTerms);
		const response = await csrfFetch("/api/auth/login", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				email: email.toLowerCase(),
				password,
				force_login: forceLogin,
				agreed_to_terms: agreedToTerms
			}),
		});

		const data = await response.json();
		console.log("Login response:", data);

		if (!response.ok) {
			console.error("Login failed:", data);
			return data.errors;
		}

		// Check if the response indicates an active session
		if (data.hasActiveSession) {
			console.log("Active session detected:", data.sessionInfo);

			// Check if the active session was timed out
			if (data.sessionInfo && data.sessionInfo.timed_out) {
				console.log("Previous session was timed out, proceeding with login");
				// Skip the confirmation and proceed with login
				dispatch(clearActiveSession());
				dispatch(setUser(data));

				// Store the session ID in localStorage
				if (data.session_id) {
					localStorage.setItem('sessionId', data.session_id);
				}

				return data;
			}

			// Normal active session case
			dispatch(setActiveSession(data.sessionInfo));
			return { hasActiveSession: true };
		}

		// Check if the previous session was timed out
		if (data.previous_session_timed_out) {
			console.log("Previous session was timed out:", data.previous_session_info);
			// No need to show confirmation modal, just proceed with login
		}

		// Clear any active session info
		dispatch(clearActiveSession());

		// Normal login success
		dispatch(setUser(data));

		// Store the session ID in localStorage
		if (data.session_id) {
			localStorage.setItem('sessionId', data.session_id);
		}

		return data;
	} catch (error) {
		console.error("Login error:", error);

		// Check if this is a CSRF token error
		if (isCSRFTokenError(error)) {
			console.log("CSRF token error detected, attempting to refresh token and retry login");

			try {
				// Refresh the CSRF token
				await refreshCSRFToken();

				// Retry the login with the new token
				console.log("Retrying login with fresh CSRF token");
				return dispatch(login(email, password, forceLogin, agreedToTerms));
			} catch (refreshError) {
				console.error("Failed to refresh CSRF token:", refreshError);
				return ["Session expired. Please refresh the page and try again."];
			}
		}

		return ["An error occurred. Please try again."];
	}
};

export const logout = (isAutoLogout = false) => async (dispatch) => {
	// Get the session ID from localStorage
	const sessionId = localStorage.getItem('sessionId');

	const response = await fetch("/api/auth/logout", {
		headers: {
			"Content-Type": "application/json",
			"X-Session-ID": sessionId || '',
			"X-Auto-Logout": isAutoLogout ? 'true' : 'false'
		},
	});

	if (response.ok) {
		// Clear the session ID from localStorage
		localStorage.removeItem('sessionId');

		dispatch(clearSession());
    	dispatch(removeUser());
    	// Clear other relevant states
    	dispatch({ type: 'accounts/CLEAR_ACCOUNTS' });
	}
};

export const signUp = (userData) => async (dispatch) => {
	const response = await csrfFetch("/api/auth/signup", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(userData),
	});

	if (response.ok) {
		await response.json();
		// dispatch(setUser(data));
		return null;
	} else if (response.status < 500) {
		const data = await response.json();
		if (data.errors) {
			return data.errors;
		}
	} else {
		return ["An error occurred. Please try again."];
	}
};

export const changePassword = (password) => async (dispatch) => {
    try {
        const response = await csrfFetch("/api/auth/change-password", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ password }),
        });

        if (!response.ok) {
            const data = await response.json();
            return data.errors || ["An error occurred while changing your password"];
        }

        // Update the first_login flag in the store
        dispatch(updateFirstLogin(false));
        return null;
    } catch (error) {
        console.error("Password change error:", error);
        return ["An unexpected error occurred. Please try again."];
    }
};

export default function reducer(state = initialState, action) {
	switch (action.type) {
		case SET_USER:
			return { ...state, user: action.payload };
		case REMOVE_USER:
			return { ...state, user: null };
        case SET_ACTIVE_SESSION:
            return { ...state, activeSession: action.payload };
        case CLEAR_ACTIVE_SESSION:
            return { ...state, activeSession: null };
        case CLEAR_SESSION:
            return initialState;
        case UPDATE_FIRST_LOGIN:
            return {
                ...state,
                user: state.user ? { ...state.user, first_login: action.payload } : null
            };
		default:
			return state;
	}
}
